import React from 'react';
import { Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import styled from '@emotion/styled';
import {
  FindingCardBox, FindingIconBox, FindingBody,
  FindingEyebrow, FindingHeadline, FindingQuote, FindingSourceRow,
} from '../styles';

export type FindingAccent = 'critical' | 'warning' | 'info' | 'success' | 'neutral' | 'violet';

const ACCENT_HEX: Record<FindingAccent, string> = {
  critical: '#ef4444',
  warning: '#f97316',
  info: '#3b82f6',
  success: '#22c55e',
  neutral: '#94a3b8',
  violet: '#a78bfa',
};

const HeadlineRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const InfoBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(124, 58, 237, 0.12);
  color: #a78bfa;
  cursor: help;
  flex-shrink: 0;
  transition: background 0.15s ease;
  &:hover { background: rgba(124, 58, 237, 0.22); }
`;

interface Props {
  icon: LucideIcon;
  accent?: FindingAccent;
  dominant?: boolean;
  iconLg?: boolean;
  eyebrow?: React.ReactNode;
  headline: React.ReactNode;
  /** Optional explanation shown via a small ⓘ next to the headline (native browser tooltip). */
  info?: string;
  quote?: React.ReactNode;
  source?: React.ReactNode;
}

export const FindingCard: React.FC<Props> = ({
  icon: Icon, accent = 'critical', dominant, iconLg,
  eyebrow, headline, info, quote, source,
}) => (
  <FindingCardBox dominant={dominant}>
    <FindingIconBox accent={ACCENT_HEX[accent]} lg={iconLg || dominant}>
      <Icon size={iconLg || dominant ? 20 : 18} strokeWidth={2} />
    </FindingIconBox>
    <FindingBody>
      {eyebrow && <FindingEyebrow>{eyebrow}</FindingEyebrow>}
      <FindingHeadline dominant={dominant}>
        <HeadlineRow>
          {headline}
          {info && (
            <InfoBubble title={info} aria-label={info}>
              <Info size={11} strokeWidth={2.5} />
            </InfoBubble>
          )}
        </HeadlineRow>
      </FindingHeadline>
      {quote && <FindingQuote>{quote}</FindingQuote>}
      {source && <FindingSourceRow>{source}</FindingSourceRow>}
    </FindingBody>
  </FindingCardBox>
);
